import React, { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import styles from "./AmountField.module.css";

interface IAmountFieldProps {
    accountingRowIndex: number;
}

const AmountFieldBase = (props: IAmountFieldProps) => {
    const { t } = useTranslation();
    const { invoiceStore } = useContext(RootStoreContext);
    const { accountingRowIndex } = props;

    const displayedAmount = invoiceStore.currentInvoice?.displayedAmounts[accountingRowIndex] || "";

    const onChangeAmountInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            const cursorPosition = e.target.selectionStart;

            // Remove apostrophes for processing
            const cleanValue = newValue.replace(/'/g, "");
            const numericValue = parseFloat(cleanValue);

            // Update displayed amount in store
            invoiceStore.currentInvoice?.setDisplayedAmount(newValue, accountingRowIndex);

            // Update store with clean numeric value
            if (!isNaN(numericValue)) {
                invoiceStore.updateAccountingWithAmount(String(numericValue), accountingRowIndex);
            }

            // Restore cursor position after React re-renders
            setTimeout(() => {
                if (e.target.setSelectionRange && cursorPosition !== null) {
                    e.target.setSelectionRange(cursorPosition, cursorPosition);
                }
            }, 0);
        },
        [invoiceStore, accountingRowIndex]
    );

    const onFocusAmountInput = useCallback(() => {
        invoiceStore.currentInvoice?.setAmountFieldFocused(true, accountingRowIndex);
    }, [invoiceStore, accountingRowIndex]);

    const onBlurAmountInput = useCallback(() => {
        invoiceStore.currentInvoice?.setAmountFieldFocused(false, accountingRowIndex);
    }, [invoiceStore, accountingRowIndex]);

    return (
        <UI.Input
            label={t("screens.kredi_flow.accountings.amount").toString()}
            className={styles.AccountingAmountInput}
            type="text"
            autoComplete="off"
            value={displayedAmount}
            onChange={onChangeAmountInput}
            onFocus={onFocusAmountInput}
            onBlur={onBlurAmountInput}
        />
    );
};

export const AmountField = Sentry.withProfiler(observer(AmountFieldBase));
